import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { FaPlay, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import "yet-another-react-lightbox/plugins/counter.css";

import videoBg from "../../assets/meadows-images/Entry-vessella-meadows.jpg";

// Dynamically load all 25 images from the gallery-images folder of meadows-images
const importAll = (r) => r.keys().map(r);
const rawImages = importAll(
  require.context("../../assets/meadows-images/gallery-images", false, /\.(png|jpe?g|svg|avif|webp)$/i)
);

// Map imported modules to plain image source strings
const galleryImages = rawImages.map((img) =>
  typeof img === "string" ? img : img.default || img
);

export default function VideoTourMeadows() {
  const videoUrl = "https://youtu.be/7TxLoJybGIE?si=cPxAtHHpP4t6PNGm";

  const [currentIndex, setCurrentIndex] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const autoPlayRef = useRef(null);

  // Responsive items count
  const getVisibleCount = () => {
    if (windowWidth < 576) return 2; // 2 on mobile
    if (windowWidth < 992) return 3; // 3 on tablet
    return 5; // 5 on desktop
  };

  const visibleCount = getVisibleCount();

  // Create infinite sliding list by appending duplicate items at the end
  const slidingImages = [...galleryImages, ...galleryImages.slice(0, visibleCount)];

  // Resize handler
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Seamless infinite loop trigger
  useEffect(() => {
    if (currentIndex === galleryImages.length) {
      const timer = setTimeout(() => {
        setTransitionEnabled(false);
        setCurrentIndex(0);
      }, 600); // Wait for the transition to finish (600ms)
      return () => clearTimeout(timer);
    }
  }, [currentIndex, galleryImages.length]);

  // Turn transition back on after resetting position
  useEffect(() => {
    if (!transitionEnabled) {
      const timer = setTimeout(() => {
        setTransitionEnabled(true);
      }, 20);
      return () => clearTimeout(timer);
    }
  }, [transitionEnabled]);

  // Autoplay function
  const startAutoPlay = () => {
    stopAutoPlay();
    autoPlayRef.current = setInterval(() => {
      setCurrentIndex((prev) => prev + 1);
    }, 3000);
  };

  const stopAutoPlay = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  };

  // Start autoplay on mount/change
  useEffect(() => {
    startAutoPlay();
    return () => stopAutoPlay();
  }, [galleryImages.length]);

  const handlePrev = () => {
    stopAutoPlay();
    if (currentIndex === 0) {
      // Jump instantly to duplicate end element first
      setTransitionEnabled(false);
      setCurrentIndex(galleryImages.length);
      // Then slide back with transition to end - 1
      setTimeout(() => {
        setTransitionEnabled(true);
        setCurrentIndex(galleryImages.length - 1);
      }, 20);
    } else {
      setCurrentIndex((prev) => prev - 1);
    }
    startAutoPlay();
  };

  const handleNext = () => {
    stopAutoPlay();
    if (currentIndex >= galleryImages.length) {
      // Already at the end/resetting, jump immediately to 0 and transition to 1
      setTransitionEnabled(false);
      setCurrentIndex(0);
      setTimeout(() => {
        setTransitionEnabled(true);
        setCurrentIndex(1);
      }, 20);
    } else {
      setCurrentIndex((prev) => prev + 1);
    }
    startAutoPlay();
  };

  const handleImageClick = (idx) => {
    // Map duplicate indices back to the original index
    const originalIndex = idx % galleryImages.length;
    setLightboxIndex(originalIndex);
    setLightboxOpen(true);
  };

  // Lightbox slides mapping (only show the original images in the lightbox)
  const lightboxSlides = galleryImages.map((src) => ({ src }));

  return (
    <section className="meadows-video-section text-center" id="meadows-video-tour">
      <div className="container">
        {/* Title */}
        <div className="row justify-content-center mb-5">
          <div className="col-lg-8">
            <span className="meadows-video-kicker">Experience Gated Luxury</span>
            <h2 className="meadows-video-title">Virtual Video Tour</h2>
            <div className="meadows-video-title-line mx-auto mt-3 mb-4"></div>
            <p className="meadows-video-desc text-muted">
              Step inside Vessella Meadows. Take a virtual walkthrough of our premier villa community, displaying the gorgeous modern spaces, lush landscapes, and exceptional clubhouse design.
            </p>
          </div>
        </div>

        {/* Video Player Card */}
        <div className="row justify-content-center mb-5 pb-4">
          <div className="col-lg-10">
            <a
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="d-block"
              style={{ textDecoration: "none" }}
            >
              <motion.div
                className="meadows-video-wrapper position-relative overflow-hidden shadow-lg"
                whileHover={{ scale: 1.012 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                <img
                  src={videoBg}
                  alt="Vessella Meadows Video Tour Preview"
                  className="meadows-video-thumbnail w-100 h-100"
                />
                <div className="meadows-video-overlay position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center">
                  <motion.div
                    className="meadows-video-play-btn d-flex align-items-center justify-content-center border-0"
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Play video tour"
                  >
                    <FaPlay className="play-icon text-white ms-1" size={24} />
                    <span className="play-ripple ripple-1"></span>
                    <span className="play-ripple ripple-2"></span>
                  </motion.div>
                  <span className="meadows-video-play-text mt-4 text-white text-uppercase tracking-wider">Watch Video Tour</span>
                </div>
              </motion.div>
            </a>
          </div>
        </div>

        {/* Gallery Divider */}
        {/* <div className="row justify-content-center mt-5 mb-4">
          <div className="col-lg-8">
            <span className="meadows-video-kicker">Project Walkthrough Gallery</span>
            <h3 className="meadows-gallery-title font-serif text-dark">Real Development Highlights</h3>
            <div className="meadows-video-title-line mx-auto mt-2"></div>
          </div>
        </div> */}

        {/* Sliding Gallery Section */}
        <div className="meadows-gallery-slider-outer position-relative px-md-5">
          {/* Track Container */}
          <div
            className="meadows-gallery-slider-track-container overflow-hidden w-100"
            onMouseEnter={stopAutoPlay}
            onMouseLeave={startAutoPlay}
          >
            <div
              className="meadows-gallery-slider-track d-flex"
              style={{
                width: "100%",
                transform: `translateX(-${currentIndex * (100 / visibleCount)}%)`,
                transition: transitionEnabled ? "transform 0.6s cubic-bezier(0.25, 1, 0.5, 1)" : "none"
              }}
            >
              {slidingImages.map((src, idx) => (
                <div
                  key={idx}
                  className="meadows-gallery-slide-item px-2"
                  style={{ width: `${100 / visibleCount}%`, flexShrink: 0 }}
                >
                  <motion.div
                    className="meadows-gallery-image-card position-relative overflow-hidden shadow-sm"
                    whileHover={{ y: -6, scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                    onClick={() => handleImageClick(idx)}
                  >
                    <img
                      src={src}
                      alt={`Gallery Highlight ${idx + 1}`}
                      className="meadows-gallery-slide-img w-100 h-100"
                      loading="lazy"
                    />
                    <div className="meadows-gallery-slide-overlay position-absolute inset-0 d-flex align-items-center justify-content-center">
                      <span className="meadows-gallery-zoom-badge bg-dark text-white rounded-circle d-flex align-items-center justify-content-center">
                        🔍
                      </span>
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Controls */}
          <button
            className="meadows-gallery-nav-btn prev position-absolute border-0 d-flex align-items-center justify-content-center shadow"
            onClick={handlePrev}
            aria-label="Previous images"
          >
            <FaChevronLeft size={16} />
          </button>
          <button
            className="meadows-gallery-nav-btn next position-absolute border-0 d-flex align-items-center justify-content-center shadow"
            onClick={handleNext}
            aria-label="Next images"
          >
            <FaChevronRight size={16} />
          </button>
        </div>

      </div>

      {/* Full Screen Lightbox Player with Controls */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={lightboxSlides}
        index={lightboxIndex}
        plugins={[Zoom, Counter, Fullscreen]}
        imageFit="contain"
        zoom={{
          maxZoomPixelRatio: 4,
          zoomInMultiplier: 1.5,
          doubleTapDelay: 300,
          doubleClickDelay: 300,
        }}
        on={{ view: ({ index: currentIndex }) => setLightboxIndex(currentIndex) }}
        counter={{
          container: {
            style: { top: "16px", left: "16px", right: "auto", bottom: "auto", fontSize: "14px" },
          },
        }}
        styles={{ container: { backgroundColor: "rgba(15, 12, 10, 0.97)" } }}
      />
    </section>
  );
}
